/**
 * What the browser does when the deployment refuses its session.
 *
 * `/api/*` answers 401 to a caller whose session is gone — it expired, the
 * account was disabled, or its password was changed elsewhere — and every call
 * after that is refused the same way. The reconnect loop cannot tell this from a
 * network fault, so left alone the app retries behind a "reconnecting" banner
 * while nothing can succeed and the caller has no way to learn why.
 *
 * The transition back is a RELOAD rather than an in-place swap: the boot chain
 * already owns "hold on the sign-in card until a session exists", and the plugin
 * graph is built for one account — rebuilding it under the next account is
 * exactly what a reload does and what an in-place swap would have to
 * reimplement. The reason is recorded so the sign-in card can say why it is
 * being shown instead of appearing out of nowhere.
 * @module @deepseek-ai/dsh-client-web/src/session-watch
 */

// Type-only: the refusal fact is the connection handle's, so a rename of that
// member breaks this build instead of silently disarming the watch.
import type { SessionRefusalSource } from '@deepseek-ai/dsh-client-connection/client'

/** Where a return to the sign-in card records its reason. */
export interface NoticeStorage {
  /** Read one recorded reason. */
  getItem(key: string): string | null
  /** Record a reason. */
  setItem(key: string, value: string): void
  /** Clear a reason once the card has read it. */
  removeItem(key: string): void
}

/** The storage key the sign-in card reads its reason from. */
export const SIGN_IN_NOTICE_KEY = 'dsh.sign-in-notice'

/** Shown when the browser was sent back because the deployment refused its session. */
export const SESSION_REFUSED_NOTICE = '登录状态已失效，请重新登录'

/** Watch one refusal source and react to it at most once. */
export interface DeadSessionWatch {
  /** The refusal source the connection handle publishes. */
  sessionRefusal: SessionRefusalSource
  /**
   * What to do when the session is refused. Defaults to {@link returnToSignIn};
   * a caller that supplies its own owns the whole transition.
   */
  onRefused?: () => void
}

/**
 * React to a session the deployment has refused.
 *
 * A source that already reports the refusal acts immediately: the boot chain
 * subscribes once its plugin graph is up, and the session may have died while
 * that graph was being built — waiting for the next refusal would wait for a
 * call that is refused anyway.
 * @param watch - the refusal source and the reaction.
 * @returns the disposer, which stops further reactions.
 */
export function watchDeadSession(watch: DeadSessionWatch): () => void {
  let reacted = false
  const react = (): void => {
    if (reacted) return
    reacted = true
    ;(watch.onRefused ?? returnToSignIn)()
  }
  const stop = watch.sessionRefusal.subscribe(react)
  // Subscribing before reading the snapshot costs nothing — `react` reacts once —
  // and it closes the window where a refusal lands between the two.
  if (watch.sessionRefusal.getSnapshot()) react()
  return () => {
    reacted = true
    stop()
  }
}

/**
 * Send the browser back to the sign-in card, recording why.
 *
 * The record is a `sessionStorage` entry rather than a query parameter: it
 * belongs to this tab's own transition, it must not survive a later reload, and
 * it must not appear in a URL a caller might share. A browser that refuses
 * storage (private mode, a policy) still reloads — the card then simply says
 * nothing about why.
 * @param options - overrides for the platform effects, for tests.
 * @param options.reload - the transition; defaults to reloading the page.
 * @param options.storage - where the reason is recorded; defaults to sessionStorage.
 */
export function returnToSignIn(options: { reload?: () => void; storage?: NoticeStorage } = {}): void {
  const storage = options.storage ?? storageIfAvailable()
  try {
    storage?.setItem(SIGN_IN_NOTICE_KEY, SESSION_REFUSED_NOTICE)
  } catch (error) {
    // A storage that refuses the write is not a reason to keep a dead session on
    // screen; the card shows no reason instead.
    console.warn('[web-shell] could not record the sign-in reason:', error)
  }
  const reload = options.reload ?? ((): void => { globalThis.location?.reload() })
  reload()
}

/**
 * The reason the sign-in card should show, consuming the record so a later
 * reload of the same tab does not repeat it.
 * @param storage - where the reason was recorded; defaults to sessionStorage.
 * @returns the notice, or undefined when this page load is a normal visit.
 */
export function consumeSignInNotice(storage: NoticeStorage | undefined = storageIfAvailable()): string | undefined {
  try {
    const notice = storage?.getItem(SIGN_IN_NOTICE_KEY) ?? null
    if (notice === null) return undefined
    storage?.removeItem(SIGN_IN_NOTICE_KEY)
    return notice
  } catch (error) {
    // Same as the write side: a storage that cannot be read says nothing.
    console.warn('[web-shell] could not read the sign-in reason:', error)
    return undefined
  }
}

/**
 * The tab's own storage, when the platform has one.
 * @returns sessionStorage, or undefined outside a browser or where it is refused.
 */
function storageIfAvailable(): NoticeStorage | undefined {
  try {
    return globalThis.sessionStorage
  } catch (error) {
    // A browser can refuse the property itself (storage blocked in a sandboxed
    // frame), which is the same answer as having none.
    console.warn('[web-shell] session storage is unavailable:', error)
    return undefined
  }
}
